import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faLayerGroup,
  faTriangleExclamation,
  faChevronLeft,
  faChevronRight,
  faClockRotateLeft,
  faSliders,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';
import DeckCard from './DeckCard';
import UpgradePrompt from './UpgradePrompt';
import './Flashcards.css';

/**
 * Empty-state copy, most specific reason first. Search outranks filters because it is the thing
 * the user most recently typed; when both are narrowing the list, the filter chips stay on screen
 * and the Clear-filters action is still offered below.
 */
const getEmptyState = ({ tab, scope, filtersActive, search }) => {
  if (search) {
    return {
      icon: faMagnifyingGlass,
      title: 'No decks match your search',
      body: `No results for “${search}”.`,
      showClear: filtersActive,
    };
  }
  if (filtersActive) {
    return {
      icon: faSliders,
      title: 'No decks match these filters',
      body: 'Try a lower rating or a different level.',
      showClear: true,
    };
  }
  if (tab === 'Recent') {
    return {
      icon: faClockRotateLeft,
      title: 'No recent decks',
      body: 'Decks you study show up here, most recently studied first.',
    };
  }
  if (scope === 'Public') {
    return {
      icon: faLayerGroup,
      title: 'No decks found',
      body: 'No public decks match your search yet.',
    };
  }
  return {
    icon: faLayerGroup,
    title: 'No decks found',
    body: 'Try a different search or switch tabs.',
  };
};

const DecksGrid = ({
  decks,
  entitlement,
  scope,
  tab,
  search,
  filtersActive,
  onClearFilters,
  loading,
  error,
  pagination,
  onPageChange,
  onRetry,
  onNewDeck,
  deckCardHandlers,
}) => {
  const canCreate = entitlement && entitlement.canCreate;
  const isMine = scope === 'Mine';

  if (loading) {
    return (
      <div className="fc-skeleton-grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="fc-skeleton" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="fc-state">
        <div className="fc-state-icon">
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </div>
        <h3>Couldn't load decks</h3>
        <p>Something went wrong reaching the flashcard service. Try again.</p>
        <button type="button" className="fc-btn fc-btn-primary" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  const hasDecks = decks && decks.length > 0;
  const showNewDeckCard = isMine && canCreate;
  const showMineUpgradePanel = isMine && !canCreate;
  // On "My Decks" the New Deck card normally stands in for the empty state, but an active search
  // or filter needs an explanation of its own — otherwise a narrowed-away list looks identical to
  // having no decks at all, and the user is left staring at a lone New Deck card.
  const showEmptyState =
    !hasDecks && !showMineUpgradePanel && (filtersActive || !!search || !showNewDeckCard);
  const emptyState = getEmptyState({ tab, scope, filtersActive, search });

  return (
    <>
      <div className="fc-grid">
        {showNewDeckCard && (
          <button type="button" className="fc-new-deck-card" onClick={onNewDeck}>
            <span className="fc-new-deck-icon">
              <FontAwesomeIcon icon={faPlus} />
            </span>
            New Deck
          </button>
        )}

        {decks.map((deck) => (
          <DeckCard
            key={deck.id}
            deck={deck}
            entitlement={entitlement}
            {...deckCardHandlers}
          />
        ))}
      </div>

      {showEmptyState && (
        <div className="fc-state">
          <div className="fc-state-icon">
            <FontAwesomeIcon icon={emptyState.icon} />
          </div>
          <h3>{emptyState.title}</h3>
          <p>{emptyState.body}</p>
          {emptyState.showClear && onClearFilters && (
            <button
              type="button"
              className="fc-btn fc-btn-ghost fc-btn-sm"
              onClick={onClearFilters}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {showMineUpgradePanel && (
        <UpgradePrompt
          variant="create"
          className="fc-upgrade-inline"
          requiredPlans={entitlement?.requiredPlans}
        />
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="fc-pagination">
          <button
            type="button"
            className="fc-btn fc-btn-ghost fc-btn-sm"
            disabled={!pagination.hasPreviousPage}
            onClick={() => onPageChange(pagination.pageNumber - 1)}
          >
            <FontAwesomeIcon icon={faChevronLeft} /> Prev
          </button>
          <span className="fc-page-label">
            Page {pagination.pageNumber} of {pagination.totalPages}
          </span>
          <button
            type="button"
            className="fc-btn fc-btn-ghost fc-btn-sm"
            disabled={!pagination.hasNextPage}
            onClick={() => onPageChange(pagination.pageNumber + 1)}
          >
            Next <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </>
  );
};

export default DecksGrid;
